
import { Dispatch, SetStateAction, useMemo } from 'react';
import CustomSelect from '../../CommonComponents/CustomSelect'
import { PAYLOAD_SUBTYPE_OPTIONS, PAYLOAD_TYPE_OPTIONS, PAYLOAD_TYPE_SPECIFIC_FIELDS } from '../utils/constants';
import { checkNumberInputValidation, getPayloadTypeSpecificFields, getSelectedValue } from '../utils';

interface PayloadFormProps {
    formData: any;
    setFormData: Dispatch<SetStateAction<{ [key: string]: any }>>;
    onChange: (name: string, value: string) => void;
}

const PayloadForm = ({ formData, setFormData, onChange }: PayloadFormProps) => {

    const ALL_DEFAULT_PAYLOAD_FIELDS = ['name', 'size', 'mass', 'peakPower', 'averagePower', 'voltage', 'interfaces'];
    const ALL_TYPE_SPECIFIC_FIELDS = useMemo(() => [...(PAYLOAD_TYPE_SPECIFIC_FIELDS[formData.payloadSubType as string || ''] || []), ...(PAYLOAD_TYPE_SPECIFIC_FIELDS[formData.payloadDetails as string || ''] || [])], [formData.payloadSubType, formData.payloadDetails]);

    const onToggleChange = (name: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            typeSpecificParams: {
                ...prev.typeSpecificParams,
                [name]: (formData?.typeSpecificParams[name] || []).includes(value) ?
                    (formData?.typeSpecificParams[name] as string[]).filter((item) => item !== value) :
                    [...(formData?.typeSpecificParams[name] as string[] || []), value]
            }
        }));
    };

    const changeSelectedOption = (name: string, option: { label: string, value: string }) => {
        const subTypesOptions = PAYLOAD_SUBTYPE_OPTIONS[option.value] || []
        const detailsOptions = PAYLOAD_SUBTYPE_OPTIONS[subTypesOptions?.[0]?.value || ''] || []
        let updatedFormData: any = {};
        if (name === 'payloadType') {
            updatedFormData = {
                payloadType: option.value,
                payloadSubType: subTypesOptions[0]?.value || '',
                payloadDetails: detailsOptions[0]?.value || '',
                typeSpecificParams: {}
            };
        } else {
            updatedFormData = {
                payloadType: formData.payloadType as string,
                payloadSubType: option.value,
                payloadDetails: subTypesOptions[0]?.value || ''
            }
        }
        const TYPE_SPECIFIC_FIELDS = getPayloadTypeSpecificFields(updatedFormData.payloadType, updatedFormData.payloadSubType, updatedFormData.payloadDetails).map(field => field.name);
        const ALL_FIELDS = [...ALL_DEFAULT_PAYLOAD_FIELDS]
        ALL_FIELDS.forEach(key => {
            const defaultValue = key === 'interfaces' ? [] : '';
            updatedFormData = {
                ...updatedFormData,
                [key]: formData[key] || defaultValue
            }
        });
        TYPE_SPECIFIC_FIELDS.forEach(field => {
            const defaultValue = field === 'Frequency Bands' ? [] : '';
            updatedFormData = {
                ...updatedFormData,
                typeSpecificParams: {
                    ...updatedFormData?.typeSpecificParams,
                    [field]: formData?.typeSpecificParams?.[field] || defaultValue
                }
            }
        });
        setFormData((prev) => ({
            'power_sinks': prev?.['power_sinks'] || [],
            ...updatedFormData
        }));
    }

    const onChangeVoltage = (name: string, value: string, max_length: number) => {
        if (!checkNumberInputValidation(value, max_length)) return;
        onChange(name, value);
    }

    const onChangeTypeParamas = (name: string, value: string, max_length: number) => {
        if (!checkNumberInputValidation(value, max_length)) return;
        setFormData((prev) => ({
            ...prev,
            typeSpecificParams: {
                ...prev?.typeSpecificParams,
                [name]: value
            }
        }));
    }

    return (
        <div className='w-100 d-flex flex-column gap-3'>
            <div className={`d-flex flex-column w-100`}>
                <span className="cp-text">Voltage (V)</span>
                <input
                    type="text"
                    name="voltage"
                    value={formData.voltage || ''}
                    maxLength={8}
                    onChange={(event: any) => {
                        onChangeVoltage('voltage', event.target.value, 5);
                    }}
                    required={true}
                    className="form-control"
                    placeholder={`Enter Voltage`}
                />
            </div>
            <div className="w-100 d-flex flex-row gap-2">
                <div className="d-flex flex-column flex-1">
                    <div className="cp-text">Category</div>
                    <CustomSelect
                        options={PAYLOAD_TYPE_OPTIONS}
                        placeholder={"Select Category"}
                        onChange={(option) => {
                            changeSelectedOption('payloadType', option);
                        }}
                        value={getSelectedValue(formData.payloadType as string || null)}
                    />
                </div>
                <div className="d-flex flex-column flex-1">
                    <div className="cp-text">Payload Type</div>
                    <CustomSelect
                        options={PAYLOAD_SUBTYPE_OPTIONS[formData.payloadType as string] || []}
                        placeholder="Select Payload Type"
                        onChange={(option) => {
                            changeSelectedOption('payloadSubType', option);
                        }}
                        value={getSelectedValue(formData.payloadSubType as string || null)}
                    />
                </div>
                {PAYLOAD_SUBTYPE_OPTIONS[formData.payloadSubType as string] &&
                    <div className="d-flex flex-column flex-1">
                        <div className="cp-text">Payload SubType</div>
                        <CustomSelect
                            options={PAYLOAD_SUBTYPE_OPTIONS[formData.payloadSubType as string] || []}
                            placeholder="Select Payload SubType"
                            onChange={(option) => {
                                onChange("payloadDetails", option.value)
                            }}
                            value={getSelectedValue(formData.payloadDetails as string || null)}
                        />
                    </div>
                }
            </div>
            <div className='w-100 d-flex gap-2 flex-wrap'>
                {ALL_TYPE_SPECIFIC_FIELDS.map(({ label, name, type, required, options }, index) => {
                    const even_field = index % 2 !== 0;
                    if (type === 'text') {
                        return (
                            <div key={name} className={`d-flex flex-column ${even_field ? 'flex-1' : 'w-50'}`}>
                                <span className="cp-text">{label}</span>
                                <input
                                    type="text"
                                    name={name}
                                    value={formData?.typeSpecificParams?.[name] || ''}
                                    maxLength={8}
                                    onChange={(event: any) => {
                                        onChangeTypeParamas(name, event.target.value, 8);
                                    }}
                                    required={required}
                                    className="form-control"
                                    placeholder={`Enter ${name}`}
                                />
                            </div>
                        );
                    } else if (options) {
                        return (
                            <div className="d-flex flex-column w-100 mt-2 align-items-start">
                                <label className="cp-text">{label}</label>
                                <div className='d-flex gap-2 align-items-center'>
                                    {options.map((band: string, index: number) => (
                                        <div
                                            key={index}
                                            onClick={() => onToggleChange(name, band)}
                                            className={`multi__group__selection__button ${(formData?.typeSpecificParams?.[name] || []).includes(band) && "active"}`}
                                        >
                                            {band}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    )
}

export default PayloadForm